const baseUrl = process.env.REACT_APP_BASE_API_URL;

async function fetchApi(endpoint, options = {}) {
    const url = new URL(`${baseUrl}/${endpoint}`);
    const { authToken, headers: customHeaders, ...fetchOptions } = options;

    // Verificar si localStorage está disponible y obtener datos de forma segura
    const token = authToken || getAuthToken();

    // Inicializar los headers
    const headers = new Headers();

    // Detectar si es un endpoint público (no requiere autenticación)
    const isPublicEndpoint = endpoint.includes('/embed/') ||
        endpoint.includes('/public/') ||
        endpoint.includes('/qr-register') ||
        endpoint.includes('/surveys/') ||
        endpoint.includes('/tickets/') ||
        endpoint.includes('/productos/');

    // Añadir el token si existe (solo para endpoints protegidos)
    if (token) {
        headers.append('Authorization', `Bearer ${token}`);
    } else if (!isPublicEndpoint) {
        // Solo mostrar warning para endpoints que deberían tener autenticación
        console.warn('No auth token available for request');
    }

    // Verificar si el body es FormData
    const isFormData = options.body instanceof FormData;

    // Solo establecer 'Content-Type' si el body no es FormData
    if (!isFormData) {
        headers.append('Content-Type', 'application/json; charset=utf-8');
        headers.append('Accept', 'application/json');
    }

    if (customHeaders) {
        Object.entries(customHeaders).forEach(([key, value]) => {
            headers.set(key, value);
        });
    }

    // Configurar las opciones de la solicitud
    const requestOptions = {
        ...fetchOptions,
        headers,
    };

    // Asignar el body correctamente
    if (options.body) {
        if (isFormData) {
            requestOptions.body = options.body;

        } else if (!['GET', 'DELETE'].includes(options.method)) {
            requestOptions.body = JSON.stringify(options.body);
        }
    }

    // Agregar signal si se proporciona
    if (options.signal) {
        requestOptions.signal = options.signal;
    }


    const response = await fetch(url, requestOptions);

    // Manejo de errores
    if (!response.ok) {
        let errorMessage = 'Something went wrong';
        const contentType = response.headers.get('content-type');
        let errorData = null;
        try {
            if (contentType && contentType.includes('application/json')) {
                errorData = await response.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
            } else {
                const errorText = await response.text();
                errorMessage = errorText || errorMessage;
            }
        } catch (parseError) {
            console.error('Error parsing error response:', parseError);
        }

        // Incluir código de estado en el mensaje para mejor debugging
        const statusMessage = `${response.status} ${response.statusText || ''}`.trim();
        let fullErrorMessage = errorMessage !== 'Something went wrong'
            ? `${errorMessage} (${statusMessage})`
            : `${statusMessage}: ${errorMessage}`;

        // Si hay detalles adicionales en la respuesta (especialmente para errores 403), incluirlos
        if (errorData && errorData.details) {
            const details = errorData.details;
            if (details.message) {
                fullErrorMessage = details.message;
            }
            // Agregar información de troubleshooting si está disponible
            if (details.troubleshooting && Array.isArray(details.troubleshooting)) {
                fullErrorMessage += '\n\nSugerencias:\n' + details.troubleshooting.map((tip, i) => `${i + 1}. ${tip}`).join('\n');
            }
        }

        // No loguear errores 404 como críticos (son normales cuando no hay datos)
        if (response.status !== 404) {
            console.error('API Error:', {
                status: response.status,
                statusText: response.statusText,
                message: errorMessage,
                details: errorData?.details || null,
                url: url.toString()
            });
        }

        // Crear un error con información adicional
        const error = new Error(fullErrorMessage);
        error.status = response.status;
        error.details = errorData?.details || null;
        throw error;
    }

    if (options.responseType === 'blob') {
        return await response.blob();
    }

    // Manejo de respuesta exitosa
    const contentType = response.headers.get('content-type');
    try {
        if (contentType && contentType.includes('application/json')) {
            const jsonResponse = await response.json();


            return jsonResponse;
        } else {
            // Si la respuesta no es JSON o está vacía, retornamos un objeto vacío o el texto
            const textResponse = await response.text();

            return textResponse;
        }
    } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Error parsing server response');
    }
}

export const getAuthState = () => {
    try {
        const storedData = localStorage.getItem('auth-storage-petitprime');
        return storedData ? JSON.parse(storedData)?.state : null;
    } catch (error) {
        console.warn('Error accessing localStorage:', error);
        return null;
    }
};

export const getAuthToken = () => getAuthState()?.token || null;

export const getOwnerToken = () => {
    const state = getAuthState();
    return state?.user?.token || null;
};

export const get = (endpoint, signal = null) => fetchApi(endpoint, { method: 'GET', signal });
export const post = (endpoint, body, signal = null) => fetchApi(endpoint, { method: 'POST', body, signal });
export const put = (endpoint, body, signal = null) => fetchApi(endpoint, { method: 'PUT', body, signal });
export const del = (endpoint, body, signal = null) => fetchApi(endpoint, { method: 'DELETE', body, signal });
export const patch = (endpoint, body, signal = null) => fetchApi(endpoint, { method: 'PATCH', body, signal });
export const getBlob = (endpoint, options = {}) => fetchApi(endpoint, { ...options, method: 'GET', responseType: 'blob' });
export const getWithToken = (endpoint, authToken, signal = null) => fetchApi(endpoint, { method: 'GET', authToken, signal });
export const postWithToken = (endpoint, body, authToken, signal = null) => fetchApi(endpoint, { method: 'POST', body, authToken, signal });
export const delWithToken = (endpoint, authToken, signal = null) => fetchApi(endpoint, { method: 'DELETE', authToken, signal });

// Product image operations
export const uploadProductImage = async (productoId, imageFile) => {
    const formData = new FormData();
    formData.append('imagen', imageFile);

    // Llamar a fetchApi sin establecer Content-Type, dejando que el navegador lo maneje
    return await fetchApi(`productos/${productoId}/imagen`, {
        method: 'POST',
        body: formData,
        // No establecer Content-Type aquí, se manejará automáticamente con el boundary
    });
};

export const getProductImageStatus = async (productoId) => {
    return await get(`productos/${productoId}/imagen/estado`);
};

export const deleteProductImage = async (productoId) => {
    return await del(`productos/${productoId}/imagen`);
};
